import { Address } from '../types';

const currencyInputFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const brlFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const usdNumberFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function getCurrencyDigits(value: string): string {
  return value.replace(/\D/g, '');
}

export function formatPrice(priceCents: number): string {
  const price = priceCents / 100;
  return `R$ ${price.toFixed(2).replace('.', ',')}`;
}

export function formatDate(isoString: string): string {
  // For date-only strings (YYYY-MM-DD), split manually to avoid timezone shift
  const dateOnly = isoString.length === 10 ? isoString : isoString.slice(0, 10);
  const [y, m, d] = dateOnly.split('-');
  if (y && m && d) return `${d}/${m}/${y}`;
  const date = new Date(isoString);
  return date.toLocaleDateString('pt-BR');
}

export function getAgeYears(isoString: string, today = new Date()): number | null {
  const dateOnly = isoString.length === 10 ? isoString : isoString.slice(0, 10);
  const [year, month, day] = dateOnly.split('-').map(Number);

  if (!year || !month || !day) {
    return null;
  }

  let age = today.getFullYear() - year;
  const hasNotHadBirthday = today.getMonth() + 1 < month
    || (today.getMonth() + 1 === month && today.getDate() < day);

  if (hasNotHadBirthday) {
    age -= 1;
  }

  if (age < 0 || age > 130) {
    return null;
  }

  return age;
}

export function formatBirthDateWithAge(
  isoString: string | null | undefined,
  today = new Date(),
): string {
  if (!isoString?.trim()) {
    return 'Não informado';
  }

  const formattedDate = formatDate(isoString);
  const age = getAgeYears(isoString, today);

  if (age == null) {
    return formattedDate;
  }

  return `${formattedDate} (${age} ${age === 1 ? 'ano' : 'anos'})`;
}

export function formatDateTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export function formatPhone(countryCode: string, phoneNumber: string): string {
  return `+${countryCode} ${phoneNumber}`;
}

export function formatHeight(heightM: number): string {
  return `${heightM.toFixed(2).replace('.', ',')} m`;
}

export function formatWeight(weightKg: number): string {
  return `${weightKg.toFixed(1).replace('.', ',')} kg`;
}

export function formatFullAddress(address: Address): string {
  const parts = [
    address.street,
    address.street_number ? `nº ${address.street_number}` : '',
    address.complement,
    address.neighborhood,
    address.city,
    address.state,
    address.zipcode ? `CEP: ${address.zipcode}` : '',
  ].filter(Boolean);
  return parts.join(', ');
}

export function formatBoolean(value: boolean | null | undefined): string {
  if (value === null || value === undefined) return 'Não informado';
  return value ? 'Sim' : 'Não';
}

export function formatText(value: string | null | undefined, fallback: string = 'Não informado'): string {
  if (!value || value.trim() === '') return fallback;
  return value;
}

export function formatCurrency(value: number, currency: 'BRL' | 'USD' = 'BRL'): string {
  if (currency === 'USD') {
    return `USD ${usdNumberFormatter.format(value)}`;
  }

  return brlFormatter.format(value);
}

export function formatCurrencyInput(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? currencyInputFormatter.format(value) : '';
  }

  const digits = getCurrencyDigits(value);
  if (!digits) {
    return '';
  }

  return currencyInputFormatter.format(Number.parseInt(digits, 10) / 100);
}

export function parseCurrencyInput(value: string): number | null {
  const digits = getCurrencyDigits(value);
  if (!digits) {
    return null;
  }

  return Number.parseInt(digits, 10) / 100;
}

export function getInitials(name: string): string {
  return name.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}
